import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { iamPlatformAPI } from '../services/api';
import {
  getOrCreateMobileDeviceId,
  readMobileDeviceUsername,
  saveMobileDeviceUsername,
} from '../utils/mobileDeviceLogin';
import { ORGANIZATION_DISPLAY_NAME } from '../config/branding';
import { resolveLoginErrorMessage } from '../utils/iamLoginError';
import {
  MOBILE_FIRST_LOGIN_PASSWORD_PATH,
  MOBILE_SETUP_PIN_PATH,
} from '../utils/authRoutes';

const REMEMBER_KEY = 'entrea_remember_login';
const FORM_ID = 'entrea-mobile-login-form';

const pinSuggestSkippedKey = (userId) =>
  `entrea_mobile_pin_suggest_skipped_${String(userId || '').trim()}`;

/**
 * Connexion mobile native — UI sombre type app (splash, feuille, CTA fixe).
 * Logique PIN / mémorisation appareil conservée.
 */
const LoginMobileNative = () => {
  const { t } = useTranslation('common');
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [splashVisible, setSplashVisible] = useState(true);
  const [organizationName, setOrganizationName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login, hasMobilePinForUserId } = useAuth();

  useEffect(() => {
    const splashTimer = setTimeout(() => setSplashVisible(false), 1000);
    return () => clearTimeout(splashTimer);
  }, []);

  useEffect(() => {
    getOrCreateMobileDeviceId();
    const savedDeviceUser = readMobileDeviceUsername();
    let initialUser = '';
    let goPassword = false;
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.remember && parsed?.username) {
          initialUser = String(parsed.username || '').trim();
          setRemember(true);
          goPassword = true;
        }
      }
    } catch {
      // ignore
    }
    if (savedDeviceUser) {
      initialUser = savedDeviceUser;
      goPassword = true;
      setRemember(true);
    }
    if (initialUser) {
      setUsername(initialUser);
    }
    if (goPassword) {
      setStep(2);
    }
  }, []);

  useEffect(() => {
    if (step !== 1) return;
    const q = username.trim();
    if (q.length < 2) {
      setOrganizationName('');
      return;
    }
    const id = setTimeout(async () => {
      try {
        const res = await iamPlatformAPI.loginHint(q);
        const name = res.data?.organization_name;
        setOrganizationName(typeof name === 'string' && name.trim() ? name.trim() : '');
      } catch {
        setOrganizationName('');
      }
    }, 420);
    return () => clearTimeout(id);
  }, [username, step]);

  useEffect(() => {
    if (step !== 2) return;
    const q = username.trim();
    if (q.length < 2) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await iamPlatformAPI.loginHint(q);
        if (cancelled) return;
        const name = res.data?.organization_name;
        setOrganizationName(typeof name === 'string' && name.trim() ? name.trim() : '');
      } catch {
        if (!cancelled) setOrganizationName('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, username]);

  const persistRemember = (user, success) => {
    if (!success) return;
    try {
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({ remember: true, username: user }));
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      if (!username.trim()) {
        setError(t('login.errorIdentifier'));
        return;
      }
      setStep(2);
      setPassword('');
      setShowPassword(false);
      return;
    }

    setLoading(true);
    const result = await login(username, password);
    if (result.success) {
      saveMobileDeviceUsername(username.trim());
      persistRemember(username.trim(), true);
      if (result.mustChangePassword) {
        navigate(MOBILE_FIRST_LOGIN_PASSWORD_PATH);
      } else {
        const uid = String(result.user?.id || result.user?.sub || '').trim();
        const hasPin = hasMobilePinForUserId(uid);
        const skipped =
          uid && typeof localStorage !== 'undefined'
            ? localStorage.getItem(pinSuggestSkippedKey(uid)) === '1'
            : false;
        if (!hasPin && !skipped) {
          navigate(MOBILE_SETUP_PIN_PATH);
        } else {
          navigate('/dashboard');
        }
      }
    } else {
      setError(resolveLoginErrorMessage(result, t));
    }
    setLoading(false);
  };

  const goBackToIdentifier = () => {
    setStep(1);
    setPassword('');
    setError('');
    setShowPassword(false);
  };

  const capIcon = (
    <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
    </svg>
  );

  const inputClass =
    'block w-full rounded-2xl border-2 border-slate-600 bg-slate-800/90 px-4 py-4 text-[17px] leading-snug text-white shadow-inner shadow-black/20 transition placeholder:text-slate-500 focus:border-cyan-400/60 focus:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-cyan-400/15';
  const mobileCtaClass =
    'w-full min-h-[56px] rounded-2xl bg-gradient-to-r from-cyan-500 to-[#113a5b] text-[17px] font-bold text-white shadow-[0_8px_28px_rgba(6,182,212,0.35)] transition active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:cursor-not-allowed disabled:opacity-55';

  const mobilePrimaryLabel = step === 1 ? t('login.continue') : loading ? t('login.signingIn') : t('login.signIn');

  return (
    <div className="flex min-h-[100dvh] flex-col bg-gradient-to-b from-[#050f1a] via-[#0e3558] to-[#113a5b]">
      {/* Splash */}
      <div
        className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-gradient-to-b from-[#050f1a] via-[#0e3558] to-[#113a5b] transition-opacity duration-700 ease-out ${
          splashVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden={!splashVisible}
      >
        <div className="flex h-[5.25rem] w-[5.25rem] animate-pulse items-center justify-center rounded-[1.25rem] bg-white/12 ring-2 ring-white/25 backdrop-blur-md">
          {capIcon}
        </div>
        <p className="mt-6 text-xl font-extrabold tracking-tight text-white">{ORGANIZATION_DISPLAY_NAME}</p>
        <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/50">{t('login.splashLoading')}</p>
      </div>

      {/* Barre app + marque */}
      <header className="relative shrink-0 px-4 pb-5 pt-[max(10px,env(safe-area-inset-top))]">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_-30%,rgba(255,255,255,0.14),transparent)]"
          aria-hidden
        />
        <div className="relative flex items-center justify-between gap-2">
          <Link
            to="/"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/12 text-white backdrop-blur-sm ring-1 ring-white/20 transition active:bg-white/20"
            aria-label={t('login.backHomeAria')}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div className="flex flex-1 justify-center gap-2">
            <span
              className={`h-2 rounded-full transition-all ${step === 1 ? 'w-8 bg-white' : 'w-2 bg-white/35'}`}
              aria-hidden
            />
            <span
              className={`h-2 rounded-full transition-all ${step === 2 ? 'w-8 bg-white' : 'w-2 bg-white/35'}`}
              aria-hidden
            />
          </div>
          <div className="flex w-[4.25rem] shrink-0 justify-end">
            <LanguageSwitcher variant="dark" className="origin-right" />
          </div>
        </div>

        <div className="relative mt-5 flex flex-col items-center gap-3">
          <div className="flex h-[4.25rem] w-[4.25rem] items-center justify-center rounded-[1.15rem] bg-white/14 shadow-lg shadow-black/20 ring-2 ring-white/25 backdrop-blur-md">
            {capIcon}
          </div>
          <div className="max-w-[90vw] text-center">
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <p className="text-[1.4rem] font-extrabold tracking-tight text-white">{ORGANIZATION_DISPLAY_NAME}</p>
              {organizationName ? (
                <>
                  <span className="text-xl font-light text-white/35" aria-hidden>
                    ·
                  </span>
                  <p className="max-w-[min(72vw,18rem)] truncate text-[0.95rem] font-semibold leading-tight text-cyan-100">
                    {organizationName}
                  </p>
                </>
              ) : null}
            </div>
            <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/55">Uruganda Platform</p>
          </div>
        </div>
      </header>

      {/* Feuille sombre */}
      <div className="mx-auto flex w-full max-w-lg min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-[1.9rem] bg-slate-900 shadow-[0_-10px_40px_rgba(0,0,0,0.35)] ring-1 ring-white/10">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-6">
            <div className="mx-auto w-full max-w-md">
              <h1 className="text-left text-2xl font-extrabold leading-tight tracking-tight text-white">
                {step === 1 ? t('login.welcomeTitle') : t('login.passwordTitle')}
              </h1>
              <p className="mt-2 text-left text-[15px] text-slate-400">
                {step === 1 ? t('login.subtitleStep1') : t('login.subtitleStep2')}
              </p>

              {error && (
                <div
                  className="mt-5 rounded-2xl border border-red-800/60 bg-red-950/50 px-4 py-3 text-[15px] text-red-100"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <form id={FORM_ID} onSubmit={handleSubmit} className="mt-6 space-y-5">
                {step === 1 && (
                  <div>
                    <label
                      htmlFor="mobile-login-user"
                      className="mb-2 block text-xs font-extrabold uppercase tracking-[0.08em] text-slate-500"
                    >
                      {t('login.identifierLabel')}
                    </label>
                    <input
                      id="mobile-login-user"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      className={inputClass}
                      placeholder={t('login.identifierPlaceholder')}
                      autoFocus
                    />
                    {organizationName ? (
                      <p className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                        <span className="inline-flex h-6 items-center rounded-full bg-emerald-500/20 px-2 text-xs font-bold uppercase tracking-wide text-emerald-300">
                          {t('login.orgBadge')}
                        </span>
                        <span className="font-semibold text-cyan-100">{organizationName}</span>
                      </p>
                    ) : null}
                  </div>
                )}

                {step === 2 && (
                  <>
                    <div className="flex items-center justify-between gap-2 rounded-2xl border border-slate-600 bg-slate-800/60 px-4 py-3.5">
                      <p className="min-w-0 text-[15px] text-slate-300">
                        <span className="text-slate-500">{t('login.accountLabel')}</span>
                        <br />
                        <span className="font-bold text-white">{username}</span>
                      </p>
                      <button
                        type="button"
                        onClick={goBackToIdentifier}
                        className="shrink-0 rounded-full border border-slate-500 bg-slate-700 px-3.5 py-2 text-sm font-bold text-cyan-200 shadow-sm active:scale-95"
                      >
                        {t('login.change')}
                      </button>
                    </div>
                    <div>
                      <label
                        htmlFor="mobile-login-pass"
                        className="mb-2 block text-xs font-extrabold uppercase tracking-[0.08em] text-slate-500"
                      >
                        {t('login.passwordLabel')}
                      </label>
                      <div className="relative">
                        <input
                          id="mobile-login-pass"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                          className={`${inputClass} pr-14`}
                          placeholder="••••••••"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-700/50 hover:text-white"
                          aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex min-h-[48px] items-center gap-3 rounded-2xl border border-slate-700/80 bg-slate-800/30 px-3 py-2">
                      <input
                        id="mobile-remember"
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="h-6 w-6 shrink-0 rounded-lg border-slate-500 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                      />
                      <label htmlFor="mobile-remember" className="text-[15px] leading-snug text-slate-400">
                        {t('login.rememberMe')}
                      </label>
                    </div>
                  </>
                )}
              </form>

              <nav
                className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-t border-slate-700/60 pt-6 text-center text-sm"
                aria-label={t('login.helpToggle')}
              >
                <Link
                  to="/forgot-password?topic=username"
                  className="inline-flex min-h-[44px] items-center px-1 font-medium text-cyan-200 underline decoration-cyan-400/45 underline-offset-2 hover:text-white"
                >
                  {t('login.forgotUsername')}
                </Link>
                <span className="select-none text-slate-600" aria-hidden>
                  ·
                </span>
                <Link
                  to="/forgot-password?topic=password"
                  className="inline-flex min-h-[44px] items-center px-1 font-medium text-cyan-200 underline decoration-cyan-400/45 underline-offset-2 hover:text-white"
                >
                  {t('login.forgotPassword')}
                </Link>
              </nav>

              <p className="mt-8 border-t border-slate-700/60 pt-6 text-center">
                <Link
                  to="/"
                  className="inline-flex min-h-[48px] items-center justify-center text-[15px] font-bold text-cyan-300 underline decoration-2 decoration-cyan-500/40 underline-offset-4"
                >
                  {t('login.backHome')}
                </Link>
              </p>

              <p className="mt-4 text-center">
                <Link
                  to="/login?web=1"
                  className="text-sm font-medium text-slate-400 underline underline-offset-2 hover:text-cyan-200"
                >
                  {t('loginMobile.classicWebLink')}
                </Link>
              </p>

              <footer className="mt-8 pb-4 text-center text-[11px] text-slate-500">
                <p className="mb-2">
                  © {new Date().getFullYear()} {ORGANIZATION_DISPLAY_NAME} · Uruganda Platform
                </p>
              </footer>
            </div>
          </div>

          {/* Barre d'action fixe */}
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-700/90 bg-slate-900/95 px-4 pt-3 backdrop-blur-xl pb-[max(12px,env(safe-area-inset-bottom))]">
            <button type="submit" form={FORM_ID} disabled={loading} className={mobileCtaClass}>
              {loading && step === 2 ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t('login.signingIn')}
                </span>
              ) : (
                mobilePrimaryLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginMobileNative;
